import { Model, fields } from '@gasman/smush';
import { writeSTC } from 'stcformat';

import { Pattern } from './pattern';
import { FORMAT_NAME } from './format';
import { Sample } from '../soundtracker/sample';
import { Ornament } from '../soundtracker/ornament';
import { UndoBuffer } from '../../common/undo';

class Song extends Model([
    new fields.IntegerField('tempo', { min: 1, max: 15, default: 5 }),
    new fields.IntegerField('length', {
        label: 'Song len',
        min: 1,
        max: 255,
        default: 1,
    }),
    new fields.LazyModelListField('samples', Sample, {
        elementName: 'sample',
        startIndex: 1,
        endIndex: 16,
    }),
    new fields.LazyModelListField('ornaments', Ornament, {
        elementName: 'ornament',
        startIndex: 1,
        endIndex: 16,
        defaultData: [],
    }),
    new fields.LazyListField(
        'positions',
        new fields.StructField('position', [
            new fields.IntegerField('pattern', {
                min: 1,
                max: 31,
                default: 1,
                label: 'Pattern',
            }),
            new fields.IntegerField('transpose', {
                min: -127,
                max: 127,
                default: 0,
                label: 'Transpose',
            }),
        ]),
        { startIndex: 1 },
    ),
    new fields.LazyModelListField('patterns', Pattern, {
        elementName: 'pattern',
        startIndex: 1,
    }),
]) {
    constructor(songData) {
        super(songData);
        this.undoBuffer = new UndoBuffer();
    }

    getChannelCount() {
        let Pattern = this.constructor.fields.patterns.model;
        return Pattern.fields.channels.fieldList.length;
    }

    serialize() {
        let result = super.serialize();
        result['engine'] = FORMAT_NAME;
        return result;
    }

    asBlob() {
        return new Blob([JSON.stringify(this.serialize())], {
            type: 'application/json',
        });
    }

    undo() {
        this.undoBuffer.undo(this);
    }
    redo() {
        this.undoBuffer.redo(this);
    }

    exportFormats = ['2xSTC'];
    exportToFormat(format) {
        if (format === '2xSTC') {
            const songData = this.serialize();
            songData.samples = [null, ...songData.samples];
            songData.ornaments = [
                {
                    tones: [
                        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
                        0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
                    ],
                },
                ...songData.ornaments,
            ];
            const chip1Patterns = songData.patterns.map((pattern) => {
                return {
                    length: pattern.length,
                    channels: pattern.channels.slice(0, 3),
                };
            });
            const chip2Patterns = songData.patterns.map((pattern) => {
                return {
                    length: pattern.length,
                    channels: pattern.channels.slice(3, 6),
                };
            });

            songData.patterns = [null, ...chip1Patterns];
            const stc1 = writeSTC(songData);

            songData.patterns = [null, ...chip2Patterns];
            const stc2 = writeSTC(songData);

            return {
                files: [
                    { data: stc1, filename: 'song1.stc' },
                    { data: stc2, filename: 'song2.stc' },
                ],
            };
        } else {
            throw new Error(`Unsupported export format: ${format}`);
        }
    }

    convertTargetEngine = 'Soundtracker';
    convertToOtherEngine() {
        const songData = this.serialize();
        for (let pattern of songData.patterns) {
            pattern.channels = pattern.channels.slice(0, 3);
        }
        const { SoundtrackerEngine } = require('../soundtracker');
        const convertedSong = new SoundtrackerEngine.Song(songData);
        return {
            song: convertedSong,
            engine: SoundtrackerEngine,
        };
    }
}

export { Song };
